import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "abfa-install-dismissed-at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const DELAY_MS = 4000;
const HIDDEN_ROUTES = [/^\/big-screen/, /\/big-screen$/, /\/stadium$/];

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const snoozed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < SNOOZE_MS;

// Android/Chrome: native install via beforeinstallprompt. iOS Safari: Add-to-Home-Screen guide.
export const InstallPrompt = () => {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios] = useState(isIOS);

  useEffect(() => {
    if (isStandalone() || snoozed()) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setVisible(false); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (isStandalone() || snoozed() || !(deferred || ios)) return undefined;
    const t = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [deferred, ios]);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setVisible(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferred(null);
    setVisible(false);
  };

  if (!visible || HIDDEN_ROUTES.some((r) => r.test(pathname))) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:w-[380px] z-[60] bg-[#09090b] text-white shadow-2xl border-t-4 border-[#FFC72C] install-prompt-enter" role="dialog" aria-label="Install the ABFA app" data-testid="install-prompt">
      <button onClick={dismiss} className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white" aria-label="Not now" data-testid="install-dismiss-btn">
        <X className="w-4 h-4" />
      </button>
      <div className="p-4 flex gap-4">
        <img src="/icons/icon-192.png" alt="" className="w-14 h-14 bg-white p-1 shrink-0" />
        <div className="min-w-0 pr-4">
          <p className="font-heading font-black uppercase text-lg leading-tight">Get the ABFA app</p>
          <p className="text-xs text-gray-400 mt-1">Live scores, fixtures, tables and news, one tap from your home screen.</p>
        </div>
      </div>
      {ios ? (
        <div className="px-4 pb-4 text-sm space-y-2" data-testid="install-ios-steps">
          <p className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#FFC72C] text-black text-xs font-bold flex items-center justify-center">1</span> Tap <Share className="w-4 h-4 text-[#4FD1E8]" /> <b>Share</b> in Safari</p>
          <p className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#FFC72C] text-black text-xs font-bold flex items-center justify-center">2</span> Choose <PlusSquare className="w-4 h-4" /> <b>Add to Home Screen</b></p>
          <button onClick={dismiss} className="w-full mt-1 py-2 text-xs font-bold uppercase tracking-widest bg-white/10 hover:bg-white/20" data-testid="install-ios-ok-btn">Got it</button>
        </div>
      ) : (
        <div className="px-4 pb-4 flex gap-2">
          <button onClick={install} className="flex-1 py-2.5 bg-[#FFC72C] hover:bg-[#E6B325] text-black font-heading font-bold uppercase flex items-center justify-center gap-2" data-testid="install-app-btn">
            <Download className="w-4 h-4" /> Install
          </button>
          <button onClick={dismiss} className="px-4 py-2.5 text-xs font-bold uppercase tracking-widest bg-white/10 hover:bg-white/20" data-testid="install-later-btn">Not now</button>
        </div>
      )}
    </div>
  );
};
